import {
  Checkbox,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Technique,
  TechniqueRequest,
  techniquesApi,
} from '@texas/api/endpoints/metadata/techniquesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { useActiveSession } from '@texas/hooks/useSession';

const emptyTechnique: TechniqueRequest = {
  code: '',
  value: '',
  description: '',
  displayOrder: 0,
  vdcSupport: false,
  archived: false,
};

interface TechniqueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  technique: Technique | null;
}

export function TechniqueModal({
  isOpen,
  onClose,
  onSaved,
  technique,
}: TechniqueModalProps) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const toast = useToast();
  const {
    request: create,
    loading: creating,
    error: createError,
  } = useApiRequest(techniquesApi.create);
  const {
    request: update,
    loading: updating,
    error: updateError,
  } = useApiRequest(techniquesApi.update);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<TechniqueRequest>({ defaultValues: emptyTechnique });

  useEffect(() => {
    reset(
      technique
        ? { ...technique, description: technique.description ?? '' }
        : emptyTechnique,
    );
  }, [isOpen, reset, technique]);

  const submit = async (request: TechniqueRequest) => {
    const response = technique
      ? await update(technique.id, request)
      : await create(currentUser.organizationId, request);
    if (response.hasError) return;
    toast({
      title: technique ? t('general.updated') : t('general.created'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  return (
    <Modal variant="entityDirectory" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {technique ? t('general.edit') : t('general.new')}{' '}
          {t('configuration.technique').toLowerCase()}
        </ModalHeader>
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <ErrorDetails error={createError ?? updateError} />
            <Flex direction="column" gap="4">
              <FormControl isRequired={true} isInvalid={!!errors.code}>
                <TexasFormLabel>{t('general.code')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('code', { required: true, maxLength: 2 })}
                />
                <HookFormCounter control={control} name="code" maxLength={2} />
                <ErrorLabel text={errors.code?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.value}>
                <TexasFormLabel>{t('general.value')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('value', { required: true, maxLength: 100 })}
                />
                <HookFormCounter
                  control={control}
                  name="value"
                  maxLength={100}
                />
                <ErrorLabel text={errors.value?.message} />
              </FormControl>
              <FormControl>
                <TexasFormLabel>{t('general.description')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('description', { maxLength: 200 })}
                />
                <HookFormCounter
                  control={control}
                  name="description"
                  maxLength={200}
                />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.displayOrder}>
                <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  {...register('displayOrder', {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
                <ErrorLabel text={errors.displayOrder?.message} />
              </FormControl>
              <Checkbox {...register('vdcSupport')}>
                {t('configuration.vdcSupport')}
              </Checkbox>
              {technique && (
                <Checkbox {...register('archived')}>
                  {t('general.archived')}
                </Checkbox>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <SubmitButton loading={creating || updating}>
              {t('general.save')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
